import {
  computed,
  onBeforeMount,
  onMounted,
  onUnmounted,
  provide,
  ref,
  useRouter,
  watch,
} from '#imports';
import { sidebarContentVisibilityKey, toggleSidebarKey } from 'r3ui-lib';
import { useAuthStore } from '~/stores/auth';

export default function useMain() {
  const router = useRouter();
  const pageName = computed(() => router.currentRoute.value.name);
  const sidebarVisibility = ref(true);
  const sidebarContentVisibility = ref(true);
  const authStore = useAuthStore();

  const clientWidth = ref<HTMLElement | null>(null);
  const contentWidth = ref<number | null>(null);
  let widthTimer: ReturnType<typeof setTimeout> | null = null;

  const isAuthenticated = computed(() => authStore.isAuthenticated);

  function hideSidebar() {
    sidebarVisibility.value = false;
    sidebarContentVisibility.value = false;
  }

  function showSidebar() {
    sidebarVisibility.value = true;
    sidebarContentVisibility.value = true;
  }

  function toggleSidebar() {
    if (sidebarVisibility.value) {
      hideSidebar();
      localStorage.setItem('sideBarVisibility', '0');
    } else {
      showSidebar();
      localStorage.setItem('sideBarVisibility', '1');
    }
  }

  function initSidebar() {
    if (localStorage.getItem('sideBarVisibility') === '1') {
      showSidebar();
    } else if (localStorage.getItem('sideBarVisibility') === '0') {
      hideSidebar();
    } else {
      showSidebar();
    }
  }

  const sidebarWidth = computed(() => {
    const defaultWidth = 320;
    const collapsedWidth = 60;
    if (sidebarVisibility.value) {
      return defaultWidth;
    }
    return collapsedWidth;
  });

  function updateContentWidth() {
    if (widthTimer !== null) clearTimeout(widthTimer);
    widthTimer = setTimeout(() => {
      widthTimer = null;
      const client = clientWidth.value?.offsetWidth ?? 0;
      const sidebar = sidebarWidth.value ?? 0;
      if (client <= 0 || sidebar <= 0) {
        return;
      }
      contentWidth.value = client - sidebar - 20;
    });
  }

  provide(sidebarContentVisibilityKey, sidebarContentVisibility);
  provide(toggleSidebarKey, toggleSidebar);

  watch(
    () => sidebarWidth.value,
    () => {
      updateContentWidth();
    },
    { deep: true }
  );

  onBeforeMount(() => {
    initSidebar();
    updateContentWidth();
    window.addEventListener('resize', updateContentWidth, true);
  });

  onMounted(() => {
    updateContentWidth();
  });

  onUnmounted(() => {
    window.removeEventListener('resize', updateContentWidth, true);
    if (widthTimer !== null) clearTimeout(widthTimer);
  });

  return {
    authStore,
    isAuthenticated,
    contentWidth,
    clientWidth,
    sidebarWidth,
    toggleSidebar,
    sidebarContentVisibility,
    sidebarVisibility,
    pageName,
  };
}
